import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [debugMode, setDebugMode] = useState(false);
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const user = localStorage.getItem('adminUser');
    
    if (token && user) {
      navigate('/dashboard');
    }
  }, [navigate]);

  // Toggle debug mode with keyboard shortcut
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        setDebugMode(!debugMode);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [debugMode]);

  const handleLogin = async () => {
    setIsLoading(true);
    setError('');

    const startTime = Date.now();

    try {
      const requestBody = {
        username: username.trim(),
        password: password
      };

      const response = await fetch('https://n8n.acespireconsulting.com/webhook/login1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const responseText = await response.text();

      let data;
      let loginData;

      try {
        data = JSON.parse(responseText);
        loginData = Array.isArray(data) ? data[0] : data;
      } catch (parseError) {
        console.error('❌ JSON parse error:', parseError);
        setError('Invalid response from server. Please try again.');
        return;
      }

      // Enhanced success detection
      const successIndicators = {
        responseOk: response.ok,
        hasSuccess: loginData.success === true,
        hasLoginSuccess: loginData.login_success === "true" || loginData.login_success === true,
        hasToken: !!(loginData.token || loginData.session_token),
        noError: !loginData.error && !loginData.isError,
        statusNotError: loginData.status !== 401 && loginData.status !== 400
      };

      const isSuccess = successIndicators.responseOk && 
                       (successIndicators.hasSuccess || 
                        successIndicators.hasLoginSuccess || 
                        (successIndicators.hasToken && successIndicators.noError && successIndicators.statusNotError));
      
      if (isSuccess) {
        const token = loginData.token || loginData.session_token;
        
        if (!token) {
          setError('Login failed: No authentication token received');
          return;
        }
        
        const userData = {
          id: loginData.user?.id || loginData.id,
          username: loginData.user?.username || loginData.username || username
        };
        
        if (!userData.id || !userData.username) {
          setError('Login failed: Incomplete user information');
          return;
        }
        
        try {
          localStorage.setItem('adminToken', token);
          localStorage.setItem('adminUser', JSON.stringify(userData));
        } catch (storageError) {
          console.error('❌ localStorage error:', storageError);
          setError('Login failed: Could not save login information');
          return;
        }
        
        navigate('/dashboard', { 
          replace: true,
          state: { loginSuccess: true, user: userData }
        });
        
        setUsername('');
        setPassword('');
        
      } else {
        const errorMessage = 
          loginData.error || 
          (loginData.isError ? 'Authentication failed' : null) ||
          (loginData.status === 401 ? 'Invalid username or password' : null) ||
          (loginData.status === 400 ? 'Invalid request format' : null) ||
          (!response.ok ? `Server error (${response.status})` : null) ||
          'Login failed. Please check your credentials.';
          
        setError(errorMessage);
      }
    } catch (err) {
      console.error('🚨 NETWORK/REQUEST ERROR', err);
      
      let userFriendlyError = 'Network error. Please check your connection and try again.';
      
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        userFriendlyError = 'Cannot connect to server. Please check your internet connection.';
      } else if (err.name === 'AbortError') {
        userFriendlyError = 'Request timed out. Please try again.';
      } else if (err.message.includes('CORS')) {
        userFriendlyError = 'Server configuration error. Please contact support.';
      }
      
      setError(userFriendlyError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (username.trim() && password) {
      handleLogin();
    } else {
      const missingFields = [];
      if (!username.trim()) missingFields.push('username');
      if (!password) missingFields.push('password');
      setError(`Please enter ${missingFields.join(' and ')}`);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const clearError = () => {
    setError('');
  };

  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    if (error) clearError();
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) clearError();
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
       background: 'linear-gradient(135deg, rgba(250, 247, 249, 0.99) 5%, rgba(255, 149, 229, 0.8) 30%,  rgba(121, 188, 255, 0.6) 80%)'
      }}
    >
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mb-6">
            <img 
              src="/assets/Hireon.png" 
              alt="Company Logo" 
              className="w-12 h-12 mx-auto rounded-lg object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          <p className="text-black text-lg font-medium">
            Hireon by Acespire
          </p>
        </div>

        {/* Login Form Container */}
        <div 
          className="rounded-lg shadow-lg p-8 space-y-6"
          style={{ 
            backgroundColor: '#E5E8F1',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(681px)',
            WebkitBackdropFilter: 'blur(681px)'
          }}
        >
          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
              <div className="flex justify-between items-center">
                <span>{error}</span>
                <button 
                  onClick={clearError}
                  className="text-red-700 hover:text-red-900 font-bold text-lg leading-none"
                  title="Clear error"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Username Field */}
            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={handleUsernameChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-black focus:border-transparent pr-10 bg-white transition-all duration-200"
                required
                disabled={isLoading}
                autoComplete="username"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>

            {/* Password Field */}
            <div className="relative mb-6">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={handlePasswordChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-black focus:border-transparent pr-10 bg-white transition-all duration-200"
                required
                disabled={isLoading}
                autoComplete="current-password"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors p-1"
                  tabIndex={-1}
                  disabled={isLoading}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 11-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading || !username.trim() || !password}
              className="w-full text-white py-3 rounded-lg font-medium transition-all duration-200 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
              style={{ backgroundColor: isLoading ? '#6B7280' : '#275F97' }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Logging in...
                </div>
              ) : (
                'LOG IN'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;